import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, Sun, Moon, ExternalLink, FileText } from 'lucide-react';
import { sanitizeHttpUrl } from '@airclou/utils';
import { useBrand } from '@/contexts/BrandContext';
import { useTheme } from '@/contexts/ThemeContext';

interface PortalHeaderProps {
  onSearchClick?: () => void;
  hideSearch?: boolean;
}

export function PortalHeader({ onSearchClick, hideSearch }: PortalHeaderProps) {
  const { brand, isAuthenticated } = useBrand();
  const { resolvedTheme, canToggle, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // `header_link_url` is the only branding value the portal renders as an
  // `href`, so it is the only one where a bad value is executable rather than
  // merely ugly: React does not block `javascript:` in `href`, and such a URL
  // runs in this document regardless of `target="_blank"`.
  //
  // Both write routes refuse anything but http(s) as of 2026-09-06, but neither
  // did before — the helpdesk Help Center tab stored the field as free text and
  // the KB route used `z.string().url()`, which `javascript:alert(1)` satisfies.
  // A row written under either rule is still live, so the check belongs here as
  // well: this is the same read-side re-validation the chat widget applies to
  // everything it is handed, for the same reason (a stored value is not a
  // trusted one just because a form usually writes it). Dropping the link
  // entirely is the right failure — a header link nobody can see beats one that
  // runs something.
  const headerLinkUrl = sanitizeHttpUrl(brand?.header_link_url) ?? null;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  return (
    <header
      className="sticky top-0 z-50 border-b"
      style={{
        backgroundColor: 'var(--header-bg)',
        color: 'var(--header-text)',
      }}
    >
      <div className="mx-auto max-w-[1120px] px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-2">
            {brand?.logo_url && (
              <div className="h-8 w-8 flex-shrink-0">
                <img
                  src={brand.logo_url}
                  alt={brand.name}
                  className="h-8 w-auto"
                />
              </div>
            )}
            <span className="text-xl font-semibold">{brand?.name || 'Help Center'}</span>
          </Link>

          {/* Desktop Search - hidden on home page */}
          {!hideSearch && (
            <form
              onSubmit={handleSearch}
              className="mx-4 hidden max-w-md flex-1 md:flex"
            >
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 w-full rounded-lg border bg-background pl-10 pr-4 text-foreground placeholder:text-muted-foreground transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
              </div>
            </form>
          )}

          {/* Desktop Actions */}
          <div className="hidden items-center gap-2 md:flex">
            {/* My Requests */}
            {isAuthenticated ? (
              <Link
                to="/requests"
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/50"
              >
                <FileText className="h-4 w-4" />
                My requests
              </Link>
            ) : (
              <Link
                to="/auth/login?return_to=/requests"
                className="rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/50"
              >
                Sign in
              </Link>
            )}
            {/* Custom Header Link */}
            {headerLinkUrl && brand?.header_link_text && (
              <a
                href={headerLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/50"
              >
                {brand.header_link_text}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {/* Theme Toggle */}
            {canToggle && (
              <button
                type="button"
                onClick={toggleTheme}
                className="rounded-lg p-2 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {resolvedTheme === 'dark' ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </button>
            )}
          </div>

          {/* Mobile Actions */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Theme Toggle (Mobile) */}
            {canToggle && (
              <button
                type="button"
                onClick={toggleTheme}
                className="rounded-lg p-2 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {resolvedTheme === 'dark' ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </button>
            )}
            {!hideSearch && (
              <button
                type="button"
                onClick={onSearchClick}
                className="rounded-lg p-2 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="Search"
              >
                <Search className="h-5 w-5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-lg p-2 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="border-t py-4 md:hidden space-y-2">
            {/* My Requests (Mobile) */}
            {isAuthenticated ? (
              <Link
                to="/requests"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/50"
              >
                <FileText className="h-4 w-4" />
                My requests
              </Link>
            ) : (
              <Link
                to="/auth/login?return_to=/requests"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/50"
              >
                Sign in
              </Link>
            )}
            {/* Custom Header Link (Mobile) */}
            {headerLinkUrl && brand?.header_link_text && (
              <a
                href={headerLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/50"
              >
                {brand.header_link_text}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
